import { Component, Element, Event, EventEmitter, Prop, h } from '@stencil/core';
import Bind from 'bind-decorator';

import { AriaObject } from '../../components';
import { LinkTarget } from '../../types';
import { processAriaObject } from '../../utils/aria-object';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { hasSlotted } from '../../utils/slotted';

@Component({
	shadow: true,
	styleUrl: 'zds-link.scss',
	tag: 'zds-link',
})
export class ZdsLink {
	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter<FocusEvent>;
	@Event({ bubbles: true }) zdsClick: EventEmitter<MouseEvent>;
	@Event() zdsFocus: EventEmitter<FocusEvent>;

	@Prop() iconPosition: 'start' | 'end' | 'top' | 'bottom' | undefined;
	@Prop() inherit: boolean;
	@Prop() link: string;
	@Prop() linkTarget: LinkTarget = '_self';
	@Prop() loud: boolean;
	@Prop() inputAria: AriaObject | string;

	@Bind
	onBlur(event: FocusEvent) {
		event.stopPropagation();
		this.zdsBlur.emit(event);
	}

	@Bind
	onClick(event: MouseEvent) {
		event.stopPropagation();
		this.zdsClick.emit(event);
	}

	@Bind
	onFocus(event: FocusEvent) {
		event.stopPropagation();
		this.zdsFocus.emit(event);
	}

	componentWillRender() {
		this.setDefaultIconPosition();
	}

	/**
	 * Sets default icon position if it wasn't already given, and an icon slot was used.
	 */
	setDefaultIconPosition() {
		if (!this.iconPosition && hasSlotted(this.hostElement, 'icon')) {
			this.iconPosition = 'start';
		}
	}

	render() {
		const cssClasses = generateCssClasses('zds-link', {
			[`icon`]: !!this.iconPosition,
			[`icon-${this.iconPosition}`]: !!this.iconPosition,
			inherit: this.inherit,
			loud: this.loud,
		});

		const inputAriaProps = processAriaObject(this.inputAria, 'zds-link', 'input-aria');

		return (
			<a
				class={`${cssClasses}`}
				href={this.link}
				target={this.linkTarget}
				onBlur={this.onBlur}
				onClick={this.onClick}
				onFocus={this.onFocus}
				tabindex="0"
				{...inputAriaProps}
			>
				{this.renderIcon()}
				<span class="zds-link__label">
					<slot />
				</span>
			</a>
		);
	}

	renderIcon() {
		if (!hasSlotted(this.hostElement, 'icon')) {
			return null;
		}

		return (
			<span class="zds-link__icon">
				<slot name="icon"></slot>
			</span>
		);
	}
}
